// @flow

import { $none, $set, update } from 'qim';
import {
  differenceBy,
  findIndex,
  get,
  isEmpty,
  kebabCase,
  map,
  omit,
  partial,
  toUpper,
} from 'lodash';

import {
  MUTATION_TYPES,
  SERVICE_TYPES,
  WRITE_METHODS,
} from 'app/developer-v3/constants';

import { findUniqueFields } from './field';
import { quickCopy } from './definition';

import type {
  AppService,
  BundleType,
  ServiceType,
} from 'app/developer-v3/types';
import type {
  AuthenticationSchema,
  AuthenticationType,
} from 'app/developer-v3/platformSchema/authentication';
import type { FieldSchema } from 'app/developer-v3/platformSchema/field';
import type { RequestSchema } from 'app/developer-v3/platformSchema/request';

const makeBundlize = (bundleType: BundleType) => (key: string) =>
  `{{bundle.${bundleType}.${key}}}`;
const bundlizeAuthData = makeBundlize('authData');
const bundlizeInputData = makeBundlize('inputData');

const getRequestMethod = (request, serviceType) => {
  const defaultMethod = serviceType === SERVICE_TYPES.creates ? 'POST' : 'GET';
  return request.method || defaultMethod;
};

// converts "api_key" to "X-API-KEY", and "api key" to "X-API-KEY"
const getHeaderKey = (fieldKey: string): string =>
  `X-${toUpper(kebabCase(fieldKey))}`;

const addFieldToRequest = (
  fields,
  newFields,
  request,
  bundlize,
  serviceType
) => {
  const newField = findUniqueFields(fields, newFields)[0];

  const requestMethod = getRequestMethod(request, serviceType);
  const requestKey = WRITE_METHODS.includes(requestMethod) ? 'body' : 'params';

  return update(
    [requestKey, newField.key, $set(bundlize(newField.key))],
    request
  );
};

const removeFieldFromRequest = (fields, newFields, request) => {
  const field = findUniqueFields(newFields, fields)[0];

  const newRequest = update(
    [
      ['body', field.key, $none],
      ['params', field.key, $none],
    ],
    request
  );
  if (isEmpty(newRequest.body)) {
    delete newRequest.body;
  }
  if (isEmpty(newRequest.params)) {
    delete newRequest.params;
  }

  return newRequest;
};

const updateFieldInRequest = (fields, newFields, request, bundlize) => {
  const [field] = differenceBy(fields, newFields, 'key');
  const newRequest = quickCopy(request);

  if (!field) {
    return newRequest;
  }

  const [newField] = differenceBy(newFields, fields, 'key');
  const fieldIndex = findIndex(fields, ['key', field.key]);
  const newFieldIndex = findIndex(newFields, ['key', newField.key]);
  const { key } = fields[fieldIndex];
  const { key: newKey } = newFields[newFieldIndex];
  const bundleField = bundlize(key);
  const newBundleField = bundlize(newKey);

  if (newRequest.headers) {
    delete newRequest.headers[getHeaderKey(key)];
    newRequest.headers[getHeaderKey(newKey)] = newBundleField;
  }

  if (newRequest.body) {
    delete newRequest.body[key];
    newRequest.body[newKey] = newBundleField;
  }

  if (newRequest.params) {
    delete newRequest.params[key];
    newRequest.params[newKey] = newBundleField;
  }

  if (newRequest.url) {
    newRequest.url = newRequest.url.replace(bundleField, newBundleField);
  }

  return newRequest;
};

const getFieldMutationType = (fields, newFields) => {
  if (fields.length > newFields.length) {
    return MUTATION_TYPES.remove;
  }

  if (fields.length < newFields.length) {
    return MUTATION_TYPES.add;
  }

  return MUTATION_TYPES.update;
};

const mutateFieldsInRequest = (
  bundlize: string => string,
  fields: FieldSchema[] = [],
  newFields: FieldSchema[] = [],
  request: RequestSchema,
  serviceType?: ServiceType
): RequestSchema => {
  const newRequest = quickCopy(request);

  if (request.source) {
    return newRequest;
  }

  const mutationType = getFieldMutationType(fields, newFields);
  return mutationType === MUTATION_TYPES.add
    ? addFieldToRequest(fields, newFields, newRequest, bundlize, serviceType)
    : mutationType === MUTATION_TYPES.update
    ? updateFieldInRequest(fields, newFields, newRequest, bundlize)
    : removeFieldFromRequest(fields, newFields, newRequest);
};

const mutateAuthFieldsInRequest = partial(
  mutateFieldsInRequest,
  bundlizeAuthData
);
const mutateInputFieldsInRequest = partial(
  mutateFieldsInRequest,
  bundlizeInputData
);

// Mutate headers/body/params.
const mutateAuthFieldsInTestRequest = (
  authentication: AuthenticationSchema,
  fields: Array<FieldSchema> = [],
  newFields: Array<FieldSchema> = []
): RequestSchema => {
  const authType = authentication.type;

  const newAuthentication = update(
    [
      ['fields', $set(newFields)],
      ['test', $set(get(authentication, ['test'], {}))],
    ],
    authentication
  );

  if (
    get(authentication, ['test', 'source']) ||
    get(authentication, ['test', 'require'])
  ) {
    return newAuthentication;
  }

  const newRequest = mutateAuthFieldsInRequest(
    fields,
    newFields,
    newAuthentication.test
  );

  if (authType === 'oauth2') {
    newRequest.headers = {
      ...newRequest.headers,
      Authorization: `Bearer {{bundle.authData.access_token}}`,
    };
  }

  newRequest.headers = omit(
    newRequest.headers,
    map(fields, ({ key }) => getHeaderKey(key))
  );
  newFields.forEach(({ key }) => {
    newRequest.headers = {
      ...newRequest.headers,
      [getHeaderKey(key)]: bundlizeAuthData(key),
    };
  });
  if (isEmpty(newRequest.headers)) {
    delete newRequest.headers;
  }

  if (authType !== 'oauth2') {
    newRequest.params = omit(newRequest.params, map(fields, 'key'));
    newFields.forEach(({ key }) => {
      newRequest.params = {
        ...newRequest.params,
        [key]: bundlizeAuthData(key),
      };
    });
    if (isEmpty(newRequest.params)) {
      delete newRequest.params;
    }
  }

  return update(['test', $set(newRequest)], newAuthentication);
};

// Include in request's headers/params.
const includeAuthFieldsInRequest = (
  authType: AuthenticationType,
  fields: FieldSchema[],
  request: RequestSchema
): RequestSchema => {
  if (request.source || request.require) {
    return request;
  }

  const newRequest = quickCopy(request);

  if (authType === 'oauth2') {
    newRequest.headers = {
      ...newRequest.headers,
      Authorization: `Bearer {{bundle.authData.access_token}}`,
    };
  }

  if (fields.length === 0) {
    return newRequest;
  }

  fields.forEach(({ key }) => {
    const headerKey = getHeaderKey(key);
    newRequest.headers = {
      ...newRequest.headers,
      [headerKey]: bundlizeAuthData(key),
    };
  });

  if (authType !== 'oauth2') {
    fields.forEach(({ key }) => {
      newRequest.params = {
        ...newRequest.params,
        [key]: bundlizeAuthData(key),
      };
    });
  }

  return newRequest;
};

// Include in body and/or params.
const includeInputFieldsInOperation = (
  newService: AppService,
  fields: Array<FieldSchema> = [],
  serviceType: ServiceType
): AppService => {
  const newPerform = mutateInputFieldsInRequest(
    fields,
    newService.operation.inputFields,
    newService.operation.perform,
    serviceType
  );
  return update(['operation', 'perform', $set(newPerform)], newService);
};

export {
  bundlizeAuthData,
  bundlizeInputData,
  getHeaderKey,
  includeAuthFieldsInRequest,
  includeInputFieldsInOperation,
  mutateAuthFieldsInTestRequest,
};
